// Angular Core
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnDestroy,
} from '@angular/core';
// Store
import { select, Store } from '@ngrx/store';
// Store - Reducer
import { AppState } from '@app/general/store/reducers';
import { ModuleState } from '@accounts/store/reducers';
// Store - Actions
import * as AuthActions from '@app/general/store/actions/auth.actions';
// Store - Selectors
import * as AuthSelectors from '@store/selectors/auth.selectors';
// Rxjs
import { fromEvent, Observable, Subscription } from 'rxjs';
// PrimeNG API
import { LazyLoadEvent } from 'primeng/api';
import { Paginator } from 'primeng/paginator';
import { Table } from 'primeng/table';
// Libreria
import { cloneDeep } from 'lodash';
import { formatDate } from '../../../../shared/utils/date-utils';
// Modules XLSX table
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { IExcel } from '@shared-module/interfaces';
import { IGetConfigListadoResponse } from '@shared/interfaces';

@Component({
  selector: 'sbi-cot-back-generic-table',
  templateUrl: './generic-table.component.html',
  styleUrls: ['./generic-table.component.scss'],
  standalone: false,
})
export class GenericTableComponent implements OnInit, AfterViewInit, OnDestroy {
  // default table height
  readonly defaultTableHeight = 'calc(100vh - 254px)';
  readonly minTableHeight: number = 514;
  // Roughly how many 45px rows it takes to fill minTableHeight below the
  // 53px header. Below this, the table is left at a fixed floor instead of
  // shrinking to fit; at or above it, the table grows to fit its content.
  readonly minRowsToFillDefaultHeight = 10;

  // References:
  @ViewChild('paginator', { static: false }) paginator: Paginator;
  @ViewChild('pTable', { static: true }) table: Table;

  // Inputs Table
  @Input() configListado: string = '';
  @Input() sortAndFilteringCFG: any;
  @Input() saveFilterAndSorting: string = '';
  @Input() scrollHeight: string = this.defaultTableHeight;

  @Input() set listColumns(val: any[]) {
    this.internalListColumns =
      val?.map(col => {
        const cloned = cloneDeep(col);
        if (col.template) {
          cloned.template = col.template;
        }
        return cloned;
      }) ?? [];
    this._fetchAccountListColumnsOrder();
  }

  @Input() set list(val: any[]) {
    this._internalList = val;
    this._applyTableWrapperSize();
  }

  @Input() set loading(val: boolean) {
    this._loading = val;
    this._applyTableWrapperSize();
  }

  get loading(): boolean {
    return this._loading;
  }

  @Input() totales: any[] = [];
  @Input() monedaUSD: string = 'USD';
  @Input() noCurrency: boolean = false;

  @Input() savedFilterAndSortingCriteria$: Observable<any>;

  @Input() loadingExcel: boolean = false;
  @Input() loadingFormData: boolean = false;

  // Inputs Paginator
  @Input() hasPaginator: boolean = true;
  @Input() hasResultsCount: boolean = true;
  eli;
  @Input() rows: number;
  @Input() rowsPerPageOptions: number[];
  @Input() totalTableItems: number = 0;
  @Input() totalTablePages: number = 0;

  // Input export table data
  @Input() hasExportExcel: boolean = false;
  @Input() hasExportPdf: boolean = false;
  @Input() hasClearFiltersButton: boolean = true;

  // Styles config
  @Input() hasConfigurationTableButton = true;
  @Input() customHeight = '';
  @Input() hasClearButton: boolean = true;

  // Should recieve an array, and a configurable object
  @Input() set dataToExport(excel: IExcel) {
    if (excel?.data.length > 0) this._export(excel.data, excel.fileName);
  }

  // Input checkbox custom
  @Input() customCheckboxValue: boolean = false;

  @Input() data: any;

  @Output() clearFilters = new EventEmitter();
  @Output() tableDataChange = new EventEmitter();
  @Output() paginateEvent = new EventEmitter();
  @Output() saveFilteringAndSortingConfiguration = new EventEmitter();
  @Output() rowClicked = new EventEmitter();
  @Output() exportExcel = new EventEmitter();
  @Output() exportPdf = new EventEmitter();
  @Output() clickedCustomCheckbox = new EventEmitter();

  _internalFiltersSaved = null;
  internalListColumns = null;
  _internalList = null;
  private _loading = false;
  //------ Public variables ------//

  // Button table config
  public listConfigColumns = null;
  public showActionHeader = false;
  public hasFetchedData = false;

  // Store Selectors
  public _getConfigListadoRequested$ = this._storeApp$.pipe(
    select(AuthSelectors.getConfigListadoRequested)
  );
  public _getConfigListadoResponse$ = this._storeApp$.pipe(
    select(AuthSelectors.getConfigListadoResponse)
  );
  public _setConfigListadoRequested$ = this._storeApp$.pipe(
    select(AuthSelectors.setConfigListadoRequested)
  );
  private _isAccountListColumnsOrderFetched: boolean = false;
  private _subscriptions$: Subscription[] = [];
  private _columnsSaved = [];
  private = [];
  public columnActionField = 'actions';
  private stopListening: boolean = false;
  private configListadoResponse: IGetConfigListadoResponse;

  // Constructor
  constructor(
    private _store$: Store<ModuleState>,
    private _storeApp$: Store<AppState>,
    private elementRef: ElementRef,
    private _cdRef: ChangeDetectorRef
  ) {}

  // On component init
  ngOnInit() {
    // Default sort order
    this._watchColumnOrderChanges();
    this._setTableColumns();

    this.table.sortField =
      this.sortAndFilteringCFG?.sortField || this.data?.sortField;
    this.table.sortOrder =
      this.sortAndFilteringCFG?.sortOrder || this.data?.sortOrder;
    if (this.sortAndFilteringCFG?.filters) {
      this.table.filters = this.sortAndFilteringCFG?.filters;
    }

    const tabla = document.querySelector('#tabla');
    fromEvent(tabla, 'click').subscribe(event => {
      const policyNumber = (event.target as Element).getAttribute(
        'policynumber'
      );
      if (policyNumber) {
        this.rowClicked.emit(policyNumber);
      }
    });
    this._setConfigListadoRequested();
  }

  private _setConfigListadoRequested() {
    this._subscriptions$.push(
      this._setConfigListadoRequested$.subscribe(config => {
        if (config != undefined) {
          this.loading = config;
        }
      })
    );
  }

  // Support both PrimeNG 17 (.p-datatable-wrapper) and PrimeNG 21 (.p-datatable-table-container)
  private _getTableWrapperElement(): HTMLElement | null {
    return (document.getElementsByClassName('p-datatable-wrapper')[0] ||
      document.getElementsByClassName(
        'p-datatable-table-container'
      )[0]) as HTMLElement;
  }

  private _getFixedTableHeightInPixels(): number {
    const tempElement = document.createElement('div');
    tempElement.style.height = this.defaultTableHeight;
    document.body.appendChild(tempElement);
    const computedHeight = window.getComputedStyle(tempElement).height;
    document.body.removeChild(tempElement);
    const defaultHeightInPixels = parseInt(computedHeight, 10);
    return Math.min(this.minTableHeight, defaultHeightInPixels);
  }

  private _applyTableWrapperSize(): void {
    const el = this._getTableWrapperElement();
    if (!el) return;

    if (this.customHeight) {
      el.style.height = this.customHeight;
      el.style.minHeight = this.customHeight;
      el.style.overflowX = 'auto';
      return;
    }

    const rowCount = this._internalList?.length ?? 0;
    const hasEnoughRowsToFillTheTable =
      !this._loading && rowCount >= this.minRowsToFillDefaultHeight;

    if (hasEnoughRowsToFillTheTable) {
      // Enough rows to naturally reach (or exceed) the default height:
      // let the table grow to fit its content, as it always has.
      el.style.height = 'auto';
      el.style.minHeight = 'unset';
    } else {
      // Loading, empty, or too few rows to fill the default height on
      // their own: keep a fixed floor so the table — and any loading
      // indicator centered within it — doesn't look collapsed.
      el.style.height = '';
      el.style.minHeight = `${this._getFixedTableHeightInPixels()}px`;
    }

    el.style.overflowX = 'auto';
  }

  countTitle() {
    if (!this.paginator || this.paginator.totalRecords === undefined) {
      return `${this.totalTableItems} resultados`;
    }

    let first = this.paginator.totalRecords == 0 ? 0 : this.paginator.first + 1;
    const totalRecords = this.paginator.totalRecords;
    let last = this.paginator.first + this.paginator.rows;
    if (last > totalRecords) last = totalRecords;
    if (first > last) first = 1;
    return `${first}-${last} de ${totalRecords} resultados`;
  }

  change;
  ngAfterViewInit() {
    // Para un futuro establecer un criterio de ordenamiento multiple
    // this.dataTable.onSort.subscribe((data) => {
    // });
    //this._getFilters();
    this.change = true;

    if (this.data) {
      if (this.paginator) this.paginator._first = this.data.first;
      this.table.sortField = this.data.sortField;
      this.table.sortOrder = this.data.sortOrder;
      this.table.filters = this.data.filters;
    }
    this.table.sortField = this.sortAndFilteringCFG?.sortField || '';
    this.table.sortOrder = this.sortAndFilteringCFG?.sortOrder || null;
    if (this.sortAndFilteringCFG?.filters) {
      this.table.filters = this.sortAndFilteringCFG?.filters;
    }
    if (this.sortAndFilteringCFG?.page !== 0)
      this.paginator?.changePage(this.sortAndFilteringCFG?.page);
    //Custom height by parameter
    const el = (document.getElementsByClassName('p-datatable-wrapper')[0] ||
      document.getElementsByClassName(
        'p-datatable-table-container'
      )[0]) as HTMLElement;
    if (this.customHeight && el) {
      el.style.height = this.customHeight;
    }
    if (this.customHeight && el) {
      el.style.height = this.customHeight;
    }
    this._applyTableWrapperSize();
  }

  // On component destroy
  ngOnDestroy() {
    this._subscriptions$.forEach(sub => sub.unsubscribe());
    this.elementRef.nativeElement.remove();
  }

  public customFilterCallback(filter: (a) => void, value: any): void {
    this.stopListening = true;
    filter(value);
    this.stopListening = false;
  }

  public onTableDataChange(filterAndOrderCriteria: LazyLoadEvent): void {
    if (this.stopListening) return;
    this.loading = true;
    this.tableDataChange.emit(filterAndOrderCriteria);
    this.paginator?.changePage(0);
  }

  private _fetchAccountListColumnsOrder() {
    if (this._isAccountListColumnsOrderFetched) {
      if (
        this.configListadoResponse &&
        !this.configListadoResponse.invalid &&
        this.configListadoResponse.key === this.configListado &&
        this.configListadoResponse.config
      ) {
        const columnsSaved = this.configListadoResponse.config;
        const columns = [];
        columnsSaved.forEach(element => {
          const column = this.internalListColumns.find(
            col => col.field === element.field
          );
          if (column) {
            column.visible = element.visible;
            columns.push(column);
          }
        });
        this.internalListColumns
          .filter(element => !columns.find(col => col.field === element.field))
          .forEach(element => {
            element.visible = element.config;
            columns.push(element);
          });

        this.internalListColumns = [];
        for (let i = 0; i < columns.length; i++) {
          if (!columns[i].frozen) {
            this.internalListColumns.push(columns[i]);
          }
        }
        for (let i = 0; i < columns.length; i++) {
          if (columns[i].frozen && columns[i].alignFrozen === 'right') {
            this.internalListColumns.push(columns[i]);
          }
          if (columns[i].frozen && columns[i].alignFrozen === 'left') {
            this.internalListColumns.splice(0, 0, columns[i]);
          }
        }
      }
      this._fillList();
      return;
    }
    this._store$.dispatch(
      AuthActions.getConfigListadoRequested({
        payload: { key: this.configListado },
      })
    );
    this._isAccountListColumnsOrderFetched = true;
  }

  private _fillList() {
    this.listConfigColumns = [];
    for (let i = 0; i < this.internalListColumns.length; i++) {
      if (!this.internalListColumns[i].config) {
        this.showActionHeader = true;
        continue;
      }
      this.listConfigColumns.push(this.internalListColumns[i]);
      if (this.internalListColumns.length - 1 == i) {
        this.loading = false;
      }
    }
  }

  // Set table columns
  private _setTableColumns(): void {
    this._getConfigListadoResponse$.subscribe(res => {
      if (res && !res.invalid && res.key === this.configListado && res.config) {
        this.configListadoResponse = res;
        const columnsSaved = res.config;
        const columns = [];
        columnsSaved.forEach(element => {
          const column = this.internalListColumns.find(
            col => col.field === element.field
          );
          if (column) {
            column.visible = element.visible;
            columns.push(column);
          }
        });
        this.internalListColumns
          .filter(element => !columns.find(col => col.field === element.field))
          .forEach(element => {
            element.visible = element.config;
            columns.push(element);
          });

        this.internalListColumns = [];
        for (let i = 0; i < columns.length; i++) {
          if (!columns[i].frozen) {
            this.internalListColumns.push(columns[i]);
          }
        }
        for (let i = 0; i < columns.length; i++) {
          if (columns[i].frozen && columns[i].alignFrozen === 'right') {
            this.internalListColumns.push(columns[i]);
          }
          if (columns[i].frozen && columns[i].alignFrozen === 'left') {
            this.internalListColumns.splice(0, 0, columns[i]);
          }
        }
      }
      this._fillList();
    });
  }

  private _setConfigListado(columns, filters = null) {
    let key = this.configListado;
    let config = null;
    if (columns) {
      this._columnsSaved = columns.map(element => {
        return { field: element.field, visible: element.visible };
      });
      config = this._columnsSaved;
    } else {
      key += '.Filters';
      config = filters;
    }

    this._store$.dispatch(
      AuthActions.setConfigListadoRequested({
        payload: {
          key,
          config,
        },
      })
    );
  }

  private _hasLeftFrozenColumn(): boolean {
    return !!this.internalListColumns.find(
      x => x.frozen && x.alignFrozen && x.alignFrozen === 'left'
    );
  }

  private _configLeftFrozenColumn(event): void {
    event.dragIndex++;
    event.dropIndex++;
    const column = this.internalListColumns[event.dragIndex];
    this.internalListColumns.splice(event.dragIndex, 1);
    this.internalListColumns.splice(event.dropIndex, 0, column);
    this._cdRef.detectChanges();
  }

  private _watchColumnOrderChanges() {
    this.table.onColReorder.subscribe(event => {
      for (let i = 0; i < this.internalListColumns.length; i++) {
        if (
          !event.columns.find(
            x => x.field === this.internalListColumns[i].field
          )
        ) {
          event.columns.push({
            ...this.internalListColumns[i],
            visible: false,
          });
        }
      }

      const hasLeftFrozenColumn = this._hasLeftFrozenColumn();
      if (!hasLeftFrozenColumn) {
        this.internalListColumns = [];
        for (let i = 0; i < event.columns.length; i++) {
          if (!event.columns[i].frozen) {
            this.internalListColumns.push(event.columns[i]);
          }
        }
        for (let i = 0; i < event.columns.length; i++) {
          if (
            event.columns[i].frozen &&
            event.columns[i].alignFrozen === 'right'
          ) {
            this.internalListColumns.push(event.columns[i]);
          }
          if (
            event.columns[i].frozen &&
            event.columns[i].alignFrozen === 'left'
          ) {
            this.internalListColumns.splice(0, 0, event.columns[i]);
          }
        }
      } else {
        this._configLeftFrozenColumn(event);
      }

      this._fillList();
      if (!hasLeftFrozenColumn) {
        this._setConfigListado(event.columns);
      } else {
        this._setConfigListado(this.internalListColumns);
      }
    });
  }

  onClearFilters() {
    this.clearFilters.emit();
    this.table.clear();
    this.paginator?.changePage(0);
  }

  paginate(event: any) {
    this.paginateEvent.emit(event);
  }

  onSaveConfig() {
    this._setConfigListado(this.internalListColumns);
  }

  onChangeConfig(listColumns: any[]) {
    for (let i = 0; i < this.listConfigColumns.length; i++) {
      this.listConfigColumns[i].visible = !!listColumns.find(
        x => x.header === this.listConfigColumns[i].header /*&& x.visible*/
      );
    }
    for (let i = 0; i < this.internalListColumns.length; i++) {
      this.internalListColumns[i].visible = !!this.listConfigColumns.find(
        x => x.header === this.internalListColumns[i].header && x.visible
      );
    }
  }

  getColumns() {
    // Devuelve los headers ?
    if (!this.internalListColumns || !this.listConfigColumns) return [];
    return this.internalListColumns.filter(x => {
      return (
        !x.config ||
        this.listConfigColumns.find(y => y.header === x.header && y.visible)
      );
    });
  }

  onInputDate(value, id) {
    if (value != null) this.table.filters[id][0].value = value;
  }

  public onExportExcel() {
    this.exportExcel.emit({
      rows: this.totalTableItems,
      data: this.listConfigColumns.filter(col => col.visible),
    });
  }
  public onExportPDF() {
    this.exportPdf.emit({
      rows: this.totalTableItems,
      data: this.listConfigColumns,
    });
  }

  private _export(excelData, fileName: string) {
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    this._saveAsExcelFile(excelBuffer, fileName);
  }

  private _saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    const currentDate = formatDate(new Date(), 'dd/MM/yyyy');
    FileSaver.saveAs(data, `${fileName}__${currentDate}__${EXCEL_EXTENSION}`);
  }

  toggleFilter(value: boolean) {
    this.customCheckboxValue = value;
    this.clickedCustomCheckbox.emit(this.customCheckboxValue);
    this.table.clear();
  }
}
